/**
 * Composant Toast / Notification
 * Affiche des messages temporaires à l'utilisateur
 */

import {
    createContext,
    useContext,
    useState,
    useCallback,
    type ReactNode,
} from 'react';
import { X, CheckCircle, AlertTriangle, XCircle, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

// ========================================
// Types
// ========================================

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
    id: string;
    type: ToastType;
    title: string;
    message?: string;
    duration?: number;
}

interface ToastContextType {
    toasts: Toast[];
    addToast: (toast: Omit<Toast, 'id'>) => void;
    removeToast: (id: string) => void;
    success: (title: string, message?: string) => void;
    error: (title: string, message?: string) => void;
    warning: (title: string, message?: string) => void;
    info: (title: string, message?: string) => void;
}

// ========================================
// Context
// ========================================

const ToastContext = createContext<ToastContextType | null>(null);

// ========================================
// Provider
// ========================================

interface ToastProviderProps {
    children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps): ReactNode {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const removeToast = useCallback((id: string) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const addToast = useCallback(
        (toast: Omit<Toast, 'id'>) => {
            const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
            const duration = toast.duration ?? (toast.type === 'error' ? 8000 : 5000);

            setToasts((prev) => [...prev, { ...toast, id }]);

            // Auto-remove après duration
            if (duration > 0) {
                setTimeout(() => {
                    removeToast(id);
                }, duration);
            }
        },
        [removeToast]
    );

    const success = useCallback(
        (title: string, message?: string) => {
            addToast({ type: 'success', title, message });
        },
        [addToast]
    );

    const error = useCallback(
        (title: string, message?: string) => {
            addToast({ type: 'error', title, message, duration: 10000 });
        },
        [addToast]
    );

    const warning = useCallback(
        (title: string, message?: string) => {
            addToast({ type: 'warning', title, message });
        },
        [addToast]
    );

    const info = useCallback(
        (title: string, message?: string) => {
            addToast({ type: 'info', title, message });
        },
        [addToast]
    );

    return (
        <ToastContext.Provider
            value={{ toasts, addToast, removeToast, success, error, warning, info }}
        >
            {children}
            <ToastContainer toasts={toasts} onRemove={removeToast} />
        </ToastContext.Provider>
    );
}

// ========================================
// Hook
// ========================================

export function useToast(): ToastContextType {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
}

// ========================================
// Toast Container
// ========================================

interface ToastContainerProps {
    toasts: Toast[];
    onRemove: (id: string) => void;
}

function ToastContainer({ toasts, onRemove }: ToastContainerProps): ReactNode {
    if (toasts.length === 0) return null;

    return (
        <div className="fixed top-4 right-4 z-9999 flex flex-col gap-2 max-w-sm w-full">
            {toasts.map((toast) => (
                <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
            ))}
        </div>
    );
}

// ========================================
// Toast Item
// ========================================

interface ToastItemProps {
    toast: Toast;
    onRemove: (id: string) => void;
}

function ToastItem({ toast, onRemove }: ToastItemProps): ReactNode {
    const icons = {
        success: CheckCircle,
        error: XCircle,
        warning: AlertTriangle,
        info: Info,
    };

    const styles = {
        success: 'border-[#10B981] bg-[#F0FDF4] dark:bg-[#14532D]/30',
        error: 'border-[#EF4444] bg-[#FEF2F2] dark:bg-[#7F1D1D]/30',
        warning: 'border-[#F59E0B] bg-[#FFFBEB] dark:bg-[#78350F]/30',
        info: 'border-[#0F2744] bg-[#EFF6FF] dark:bg-[#091A2E]/30',
    };

    const iconStyles = {
        success: 'text-[#10B981] dark:text-[#22C55E]',
        error: 'text-[#DC2626] dark:text-[#F87171]',
        warning: 'text-[#B45309] dark:text-[#FBBF24]',
        info: 'text-[#1D4ED8] dark:text-[#60A5FA]',
    };

    const Icon = icons[toast.type];

    return (
        <div
            className={cn(
                'flex items-start gap-3 p-4 rounded-lg border-l-4 shadow-lg bg-white dark:bg-[#1E293B]',
                'animate-in slide-in-from-right-full duration-300',
                styles[toast.type]
            )}
        >
            <Icon className={cn('h-5 w-5 flex-shrink-0 mt-0.5', iconStyles[toast.type])} />
            <div className="flex-1 min-w-0">
                <p className="font-medium text-[#1E293B] dark:text-[#F1F5F9]">{toast.title}</p>
                {toast.message && (
                    <p className="text-sm text-[#64748B] dark:text-[#94A3B8] mt-1 whitespace-pre-wrap break-words">
                        {toast.message}
                    </p>
                )}
            </div>
            <button
                onClick={() => onRemove(toast.id)}
                className="flex-shrink-0 p-1 rounded hover:bg-black/5 dark:hover:bg-white/5 transition-colors cursor-pointer"
            >
                <X className="h-4 w-4 text-[#64748B] dark:text-[#94A3B8]" />
            </button>
        </div>
    );
}

export default ToastProvider;
